import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import './ModelBackground.css';

const MODEL_URL = '/3POZ-ribbon-rainbow-vis_NIH3D.glb';

function ModelBackground() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mountNode = mountRef.current;
    if (!mountNode) {
      return undefined;
    }

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 8);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mountNode.appendChild(renderer.domElement);

    const ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
    keyLight.position.set(3, 4, 8);
    scene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0x88aaff, 0.45);
    fillLight.position.set(-5, -2, -4);
    scene.add(fillLight);

    const group = new THREE.Group();
    scene.add(group);

    const updateModelPlacement = () => {
      if (window.innerWidth <= 768) {
        group.position.set(2.8, -0.15, 0);
        return;
      }

      group.position.set(4.9, -0.25, 0);
    };

    let model = null;
    let currentRotationY = 0;
    let currentRotationX = 0;
    let targetRotationY = 0;
    let targetRotationX = 0;

    const loader = new GLTFLoader();
    loader.load(
      MODEL_URL,
      (gltf) => {
        model = gltf.scene;

        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3()).length();
        const center = box.getCenter(new THREE.Vector3());

        model.position.sub(center);
        const scale = 11.2 / size;
        model.scale.setScalar(scale);

        group.add(model);
        updateModelPlacement();
      },
      undefined,
      (error) => {
        console.error('Failed to load GLB model:', error);
      }
    );

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      updateModelPlacement();
    };

    const handleScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0 ? window.scrollY / scrollableHeight : 0;

      targetRotationY = progress * Math.PI * 0.9;
      targetRotationX = -0.12 + progress * 0.18;
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    let frameId = null;
    const animate = () => {
      frameId = window.requestAnimationFrame(animate);

      currentRotationY += (targetRotationY - currentRotationY) * 0.02;
      currentRotationX += (targetRotationX - currentRotationX) * 0.02;

      group.rotation.y = currentRotationY;
      group.rotation.x = currentRotationX;

      renderer.render(scene, camera);
    };

    animate();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);

      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }

      scene.traverse((object) => {
        if (object.isMesh) {
          object.geometry?.dispose();

          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose());
          } else {
            object.material?.dispose();
          }
        }
      });

      renderer.dispose();
      if (mountNode.contains(renderer.domElement)) {
        mountNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div className="model-bg" ref={mountRef} aria-hidden="true" />;
}

export default ModelBackground;
